import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import {
  Autocomplete,
  TextField,
  Box,
  useMediaQuery,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Stack,
  Typography,
  Chip,
  Tooltip,
  Alert,
  TablePagination,
  IconButton,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { debounce } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { AppContext } from "../../App";
import { UserProfile } from "../../types";
import axiosInstance from "../../utils/axiosInstance";
import { translate } from "../../utils/localisation";
import { PageHeader } from "../headers/PageHeader";

type UserProfilesResponse = {
  profiles: UserProfile[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;

  // Optional (only if backend supports it)
  totalCount?: number;
};

function formatDate(v?: string) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString();
}

function getFriendlyErrorMessage(e: any) {
  if (e?.__kind === "ENTITLEMENT_DENIED") return String(e?.__message || "");
  const msg = e?.response?.data?.message;
  if (typeof msg === "string" && msg.trim()) return msg;
  return String(e?.message || "Failed to load user profiles.");
}

export function UserProfileList() {
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [error, setError] = useState<string>("");

  const appContext = useContext(AppContext);
  const navigate = useNavigate();

  const theme = useTheme();
  const isTable = useMediaQuery(theme.breakpoints.up("md"));

  const [pageSize, setPageSize] = useState(25);

  const page = appContext.page; // your existing state
  const { setUserProfiles } = appContext;

  const goTo = (userId: string) => navigate(`/user-profile/${userId}`);

  const copyToClipboard = async (txt: string) => {
    try {
      await navigator.clipboard.writeText(txt);
    } catch {
      // ignore
    }
  };

  const debounceFetch = useMemo(
    () =>
      debounce(
        async (
          input: string,
          nextPage: number,
          nextPageSize: number,
          abortController: AbortController,
        ) => {
          try {
            setError("");
            const res = await axiosInstance.get<UserProfilesResponse>(
              "/admin/user-profile/",
              {
                signal: abortController.signal,
                params: {
                  pageSize: nextPageSize,
                  page: nextPage,
                  email: input,
                },
              },
            );

            const data = res.data;
            setUserProfiles(data.profiles ?? []);
            setHasNextPage(Boolean(data.hasNextPage));
            setHasPreviousPage(Boolean(data.hasPreviousPage));

            if (typeof data.totalCount === "number") {
              setTotalCount(data.totalCount);
            } else {
              setTotalCount(null);
            }
          } catch (e: any) {
            if (e?.name === "CanceledError") return;
            setError(getFriendlyErrorMessage(e));
            setUserProfiles([]);
            setHasNextPage(false);
            setHasPreviousPage(false);
            setTotalCount(null);
          } finally {
            setLoading(false);
          }
        },
        500,
      ),
    [setUserProfiles],
  );

  // Reset to page 1 when search changes or pageSize changes
  useEffect(() => {
    appContext.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, pageSize]);

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    debounceFetch(searchInput, page, pageSize, abortController);

    return () => {
      abortController.abort();
      debounceFetch.cancel();
    };
  }, [searchInput, page, pageSize, debounceFetch]);

  const showingCount = appContext.userProfiles.length;

  const canGoPrev = page > 1 && hasPreviousPage;
  const canGoNext = hasNextPage;

  const handlePrev = () => {
    if (!canGoPrev) return;
    appContext.setPage(Math.max(1, page - 1));
  };

  const handleNext = () => {
    if (!canGoNext) return;
    appContext.setPage(page + 1);
  };

  return (
    <Box
      sx={{
        overflowX: "hidden",
        maxWidth: "100%",
        mx: "auto",
        width: "100%",
        px: { xs: 1.5, sm: 2 },
        py: 1,
      }}
    >
      <PageHeader title={translate("user-profiles-screen.title-text")} />

      {/* Header / Info Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: "1px solid #E5E7EB",
          background: "#FFFFFF",
          boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
          mb: 2,
        }}
      >
        <Typography fontWeight={900} sx={{ mb: 0.5 }}>
          User Profiles
        </Typography>

        <Typography variant="body2" sx={{ color: "#6B7280", mb: 1.5 }}>
          Search by email and browse users with admin details (created/updated,
          name, id).
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
            <Chip
              size="small"
              label={`Showing: ${showingCount}`}
              sx={{
                fontWeight: 800,
                bgcolor: alpha("#10B981", 0.1),
                border: `1px solid ${alpha("#10B981", 0.25)}`,
              }}
            />
            <Chip
              size="small"
              label={`Page: ${page}`}
              sx={{
                fontWeight: 800,
                bgcolor: alpha("#111827", 0.06),
                border: `1px solid ${alpha("#111827", 0.12)}`,
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={!canGoPrev || loading}
              onClick={handlePrev}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              disabled={!canGoNext || loading}
              onClick={handleNext}
            >
              Next
            </Button>
          </Stack>
        </Stack>

        <Autocomplete
          id="user-search"
          freeSolo
          fullWidth
          options={appContext.userProfiles.map(u => u.email)}
          onInputChange={(_, v) => setSearchInput(v)}
          filterOptions={x => x}
          renderInput={params => (
            <TextField
              {...params}
              name="userSearch"
              label={translate(
                "user-profiles-screen.search-box-placeholder-text",
              )}
              size="small"
            />
          )}
        />
      </Paper>

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2, border: "1px solid #FCA5A5", bgcolor: "#FEF2F2" }}
        >
          {error}
        </Alert>
      ) : null}

      <Box sx={{ position: "relative" }}>
        {isTable ? (
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              overflowX: "auto",
              border: "1px solid #E5E7EB",
              boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
            }}
          >
            <Table stickyHeader aria-label="user profiles table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>

              <TableBody>
                {appContext.userProfiles.map((user: UserProfile) => {
                  const fullName =
                    `${user.first_name || ""} ${user.last_name || ""}`.trim();

                  return (
                    <TableRow
                      hover
                      key={user.id}
                      onClick={() => goTo(user.id)}
                      role="button"
                      tabIndex={0}
                      sx={{
                        cursor: "pointer",
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell sx={{ wordBreak: "break-word" }}>
                        {user.email}
                      </TableCell>

                      <TableCell sx={{ wordBreak: "break-word" }}>
                        {fullName || "—"}
                      </TableCell>

                      <TableCell sx={{ wordBreak: "break-all" }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="body2"
                            sx={{ wordBreak: "break-all" }}
                          >
                            {user.id}
                          </Typography>
                          <Tooltip title="Copy id">
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                void copyToClipboard(user.id);
                              }}
                            >
                              <ContentCopyRoundedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {formatDate((user as any).created_at)}
                      </TableCell>
                      <TableCell>
                        {formatDate((user as any).updated_at)}
                      </TableCell>

                      <TableCell align="right">
                        <OpenInNewIcon fontSize="small" />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading && appContext.userProfiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography fontWeight={900}>No results</Typography>
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        Try a different email search.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>

            {/* Proper pagination control (still respects hasNext/hasPrevious) */}
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={totalCount ?? -1} // -1 keeps it “unknown total” friendly
              rowsPerPage={pageSize}
              page={Math.max(0, page - 1)} // MUI is 0-based
              onPageChange={(_, nextPage0) => {
                const next = nextPage0 + 1;
                if (next < page && !hasPreviousPage) return;
                if (next > page && !hasNextPage) return;
                appContext.setPage(next);
              }}
              onRowsPerPageChange={e => setPageSize(Number(e.target.value))}
              labelDisplayedRows={({ from, to }) =>
                totalCount != null
                  ? `${from}-${to} of ${totalCount}`
                  : `${from}-${to}`
              }
            />
          </TableContainer>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              borderColor: "#E5E7EB",
              boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
            }}
          >
            <List disablePadding sx={{ width: "100%" }}>
              {appContext.userProfiles.map((user: UserProfile, idx) => {
                const fullName =
                  `${user.first_name || ""} ${user.last_name || ""}`.trim();

                return (
                  <Box key={user.id}>
                    <ListItemButton
                      onClick={() => goTo(user.id)}
                      sx={{ width: "100%", py: 1.25, alignItems: "flex-start" }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, mr: 1, mt: 0.25 }}>
                        <ChevronRightIcon />
                      </ListItemIcon>

                      <ListItemText
                        sx={{ m: 0, minWidth: 0 }}
                        primaryTypographyProps={{
                          variant: "body1",
                          fontWeight: 900,
                          sx: { wordBreak: "break-word" },
                        }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          sx: {
                            color: "#6B7280",
                            mt: 0.25,
                            whiteSpace: "pre-line",
                          },
                        }}
                        primary={user.email}
                        secondary={[
                          fullName ? `Name: ${fullName}` : "Name: —",
                          `ID: ${user.id}`,
                          `Created: ${formatDate((user as any).created_at)}`,
                          `Updated: ${formatDate((user as any).updated_at)}`,
                        ].join("\n")}
                      />

                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ ml: 1, flex: "0 0 auto" }}
                      >
                        <Tooltip title="Copy id">
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              void copyToClipboard(user.id);
                            }}
                          >
                            <ContentCopyRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <OpenInNewIcon fontSize="small" />
                      </Stack>
                    </ListItemButton>

                    {idx < appContext.userProfiles.length - 1 && (
                      <Divider component="li" />
                    )}
                  </Box>
                );
              })}

              {!loading && appContext.userProfiles.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography fontWeight={900}>No results</Typography>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    Try a different email search.
                  </Typography>
                </Box>
              ) : null}
            </List>

            <Box sx={{ p: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  variant="outlined"
                  disabled={!canGoPrev || loading}
                  onClick={handlePrev}
                >
                  Previous
                </Button>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    Rows:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={pageSize}
                    onChange={e => setPageSize(Number(e.target.value))}
                    SelectProps={{ native: true }}
                    sx={{ width: 110 }}
                  >
                    {[10, 25, 50, 100].map(n => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </TextField>
                </Stack>

                <Button
                  variant="outlined"
                  disabled={!canGoNext || loading}
                  onClick={handleNext}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              borderRadius: 3,
              backdropFilter: "blur(1px)",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
}
